import React, { useEffect, useRef, useState } from "react";
import { Camera, X, ScanBarcode, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

// BarcodeScanner — camera-based scanning modal.
// - Uses @zxing/browser which supports every 1D/2D format shipped in ZXing
//   (Code128, EAN, QR, DataMatrix, ...) so it handles both our internal
//   barcodes and any external QR/DM sticker the shop may already use.
// - Prefers the rear-facing camera on mobile; falls back to whatever device
//   is available on desktop.
// - Also exposes a plain text input as a fallback for when the user has a
//   hand-held USB scanner (which types the barcode as if it were a keyboard).
export function BarcodeScanner({ open, onOpenChange, onDetected, title = "Scan Barcode",
                                  description = "Point the camera at the tag or enter the code manually" }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);      // ZXing "controls" handle to stop the video stream
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [manual, setManual] = useState("");
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  // Enumerate cameras when the dialog opens. Called only after `open` flips true
  // so we don't request camera permission until the user actively opts in.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const cams = await BrowserMultiFormatReader.listVideoInputDevices();
        if (cancelled) return;
        setDevices(cams);
        // Rear camera on mobile is often labelled "back"/"environment" — pick that first.
        const rear = cams.find((d) => /back|rear|environment/i.test(d.label));
        setDeviceId((rear || cams[0])?.deviceId || "");
      } catch (e) {
        setError(e?.message || "Camera permission denied");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // (Re)start decoding whenever the chosen device changes.
  useEffect(() => {
    if (!open || !deviceId || !videoRef.current) return;
    let cancelled = false;
    setStarting(true);
    setError("");
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import("@zxing/browser");
        const reader = new BrowserMultiFormatReader();
        // decodeFromVideoDevice runs continuously until controls.stop() is called.
        const controls = await reader.decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
          if (cancelled) return;
          if (result) {
            const text = result.getText();
            if (text) {
              onDetected?.(text);
              // Stop the stream once we hand off — parent decides whether to close.
              try { controls?.stop(); } catch (_e) { /* ignore */ }
            }
          }
        });
        if (cancelled) { try { controls.stop(); } catch (_e) { /* ignore */ } return; }
        controlsRef.current = controls;
      } catch (e) {
        setError(e?.message || "Failed to start camera");
      } finally {
        if (!cancelled) setStarting(false);
      }
    })();
    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch (_e) { /* ignore */ }
      controlsRef.current = null;
    };
  }, [open, deviceId, onDetected]);

  // Ensure the stream is fully released the moment the dialog closes.
  useEffect(() => {
    if (!open) {
      try { controlsRef.current?.stop(); } catch (_e) { /* ignore */ }
      controlsRef.current = null;
    }
  }, [open]);

  const submitManual = () => {
    const code = manual.trim();
    if (!code) { toast.error("Enter a barcode first"); return; }
    onDetected?.(code);
    setManual("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="barcode-scanner-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl"><ScanBarcode className="h-5 w-5 text-[hsl(var(--primary))]" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {devices.length > 1 && (
            <div>
              <label className="text-xs text-muted-foreground">Camera</label>
              <Select value={deviceId} onValueChange={setDeviceId}>
                <SelectTrigger data-testid="scanner-camera-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {devices.map((d) => (
                    <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(0, 6)}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="relative rounded-lg overflow-hidden bg-black aspect-video border border-border">
            <video ref={videoRef} data-testid="scanner-video" className="w-full h-full object-cover" muted playsInline />
            {/* Framing guide — a translucent scan-line the user aims the barcode at */}
            <div className="pointer-events-none absolute inset-0 flex items-center">
              <div className="w-full h-0.5 bg-[hsl(var(--primary))]/70 shadow-[0_0_16px_hsl(var(--primary))]" />
            </div>
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" /> Starting camera...
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-xs text-center p-4">
                <div>
                  <Camera className="h-6 w-6 mx-auto mb-2" />
                  {error}
                  <div className="mt-2 text-white/70">You can still type the barcode below.</div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-muted-foreground">Or enter barcode manually</label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="scanner-manual-input" autoFocus value={manual} onChange={(e) => setManual(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && submitManual()} placeholder="e.g. MJ-EAR-0013-000001" className="font-mono" />
              <Button data-testid="scanner-manual-submit" onClick={submitManual}>Go</Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">USB hand scanners work here too — they type the code and press Enter.</p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-1"><X className="h-4 w-4" /> Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
